<template>
  <procedure-page
    :id="$route.params.id"
    :procedure="procedure"
    :result="result"
    :actions-info="actionsInfo"
    :load-func="load"
    :loading-dialog-text="loadingDialogText"
    :status="procedure.status"
    :types="types"
    :isClosed="procedure.isClosed"
    :show-cancel-details="true"
    @update:procedureGoodName="procedure.goodName = $event"
    @update:procedureType="procedure.type = $event"
    @load-error="onLoadError"
  >
    <CancellationReasonsView
      v-if="procedure.cancellationReasons?.length > 0"
      :cancellationReasons="procedure.cancellationReasons"
    />
    <contact-person
      v-if="!isHiddenContractPerson && procedure.orgFullName"
      :organization="procedure.orgFullName"
      :contact="procedure.contactPerson"
      :view="true"
    />
    <specification-order
      v-if="
        procedure.status.code === procedureStep.COMPLETED &&
        (procedure.mode === 'PARTIAL' && !procedureViewStore.needOfferPrice) &&
        result.isCurrentContragentWinner
      "
      :result="result"
      :procedure="procedure"
    />
    <winners-info
      v-if="access.canViewWinners"
      :winners="winners"
      :withPositions="procedure.withPositions"
      :procedure-step-code="procedure.status.code"
      :procedure="procedure"
    />
    <subject-info
      v-model:subject="procedure.subject"
      v-model:start-max-price="procedure.startMaxPrice"
      v-model:is-without-start-max-price="procedure.isWithoutStartMaxPrice"
      v-model:is-nds="procedure.isNds"
      v-model:additional-information="procedure.additionalInformation"
      :procedure-step-code="procedure.status.code"
      :procedure-type="procedure.type"
      :procedure="procedure"
      :result="result"
    />
    <component
      v-if="viewStageComponent"
      :is="viewStageComponent"
      :procedure="procedure"
      :supplier-requirements="supplierRequirements"
    />
    <CategoriesView
      v-if="categories.length > 0"
      :card-title="$t('Категории поставщиков')"
      :categories="categories"
    />
    <PositionsView
      v-if="positions?.length"
      :positions="positions"
      :partOfVolume="procedure.partOfVolume"
      :procedure-type="procedure.type"
      :is-nds="procedure.isNds"
      :show-start-max-price="showStartMaxPrice"
      :show-title-hint="showTitleHint"
      :show-price-quantity="showPriceQuantity"
      :table-replace-caption="tableReplaceCaption"
    />
    <file-input
      :documents="procedureDocuments"
      :download-file="downloadDocument"
      :download-all-files="() => downloadDocuments($route.params.id, ProcedureDocumentType.PROCEDURE.code)"
    >
      <template v-slot:file-icon="{ document }">
        <FileIcon :document="document" />
      </template>
    </file-input>
    <file-input
        v-if="!isGuest"
        :title="$t('Проект договора')"
        :documents="procedureDocumentsProject"
        :download-file="downloadDocument"
        :download-all-files="() => downloadDocuments($route.params.id, ProcedureDocumentType.PROJECT_AGREEMENT.code)"
    >
      <template v-slot:file-icon="{ document }">
        <FileIcon :document="document" />
      </template>
    </file-input>
    <file-input
      v-if="agreementDocuments.length && procedure.status.code != procedureStep.AGREEMENT
        && (isAdmin || isOrg)"
      :title="$t('Документы договора')"
      :documents="agreementDocuments"
      :download-file="downloadAgreementDocument"
      :download-all-files="() => downloadAgreementDocuments($route.params.id)"
    >
      <template v-slot:file-icon="{ document }">
        <FileIcon :document="document" />
      </template>
    </file-input>
    <AgreementInfo
      v-if="procedure.edoUrl && procedure.status.code != procedureStep.AGREEMENT
        && (isAdmin || isOrg)"
      v-model:agreement="procedure.edoUrl"
    />
    <OpenCloseInfo
      v-if="canViewOpenCloseInfo && procedure.allowedContragents.length"
      view
      v-model:is-closed="procedure.isClosed"
      v-model:allowedContragents="procedure.allowedContragents"
    />
  </procedure-page>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { formatMinuteTimeNoTZ } from '../filters';
import { ProcedureDocumentType } from '@/lib/file';
import api from '../api';
import config from '../config';
import procedureStep from "../components/ProcedureStep"
import ProcedurePage from "../components/ProcedurePage.vue";
import FileInput from "@intec/vue-components/form/FileInput.vue";
import SubjectInfo from "../components/procedure/SubjectInfo.vue";
import ContactPerson from '../components/procedure/ContactPerson.vue';
import AgreementInfo from "../components/procedure/AgreementInfo.vue";
import PositionsView from "../components/positions/PositionsView.vue"
import SpecificationOrder from '../components/procedure/SpecificationOrder.vue';
import WinnersInfo from '../components/procedure/WinnersInfo.vue';
import Config from "@/config";
import { useLibraryStore } from "@/stores/library";
import { useProcedureViewStore } from "@/stores/procedureViewStore";
import { useViewProcedureStageComponentStore } from "@/stores/viewProcedureStageComponentStore";
import OpenCloseInfo from "@/components/procedure/OpenCloseInfo.vue";
import CategoriesView from '@/components/categories/CategoriesView.vue';
import ProcedureStageInfo from '@/components/procedure/ProcedureStageInfo.vue';
import FileIcon from "@/components/FileIcon.vue";
import CancellationReasonsView from '@/components/procedure/CancellationReasonsView.vue';

interface Procedure {
  id?: number
  registryNumber?: string
  type?: string
  goodName?: string
  subject?: string
  goodQuantityUnits?: string
  startMaxPrice?: string
  applicationDeadline?: null
  reviewDeadline?: null
  summingStart?: null
  shippingCondition?: string
  statusText?: string
  publishedAt?: null
  organizerName?: string
  status: {
    name: string
    code: string
  }
  orgFullName?: string,
  isWithoutStartMaxPrice?: boolean,
  isNds?: boolean,
  partOfVolume: boolean
  withPositions: boolean,
  additionalInformation?: string,
  edoUrl?: string
  mode: string
  [key: string]: any
}

interface SupplierRequirement {
  type: string
  name: string
  isRequired: boolean
  description: string
  list: Array<{
    value: string
  }>
}

interface Categories {
  id: number,
  name: string
}

interface Position {
  id?: number,
  name: string,
  measure: string,
  quantity: number,
  description?: string
}

interface Access {
  canViewCompletedProcedure: boolean;
  canApplyApplication: boolean;
  canCancel: boolean;
  canEdit: boolean;
  canEditApplication: boolean;
  canRecall: boolean;
  canReview: boolean;
  canSumming: boolean;
  canView: boolean;
  canCopy: boolean;
  canViewWinners: boolean;
}

const procedure = reactive<Procedure>({
  id: null,
  registryNumber: '',
  type: '',
  goodName: '',
  subject: '',
  goodQuantityUnits: '',
  startMaxPrice: '',
  applicationDeadline: null,
  reviewDeadline: null,
  summingStart: null,
  shippingCondition: '',
  statusText: '',
  publishedAt: null,
  organizerName: '',
  status: { name: '', code: '' },
  orgFullName: '',
  isWithoutStartMaxPrice: false,
  isNds: null,
  partOfVolume: false,
  withPositions: false,
  additionalInformation: null,
  contactPerson: {
    userFullName: null,
    userPhone: null,
    userEmail: null
  },
  isClosed: false,
  allowedContragents: [],
  mode: 'ENTIRE'
});
const types = ref<{
  title: string
  value: string
}[]>([]);
const procedureActions = reactive({
  primaryAction: [],
  secondaryActions: [],
});
const supplierRequirements = reactive<Array<SupplierRequirement>>([]);
const categories = reactive<Array<Categories>>([]);
const positions = reactive<Array<Position>>([]);
const access = reactive<Access>({
  canViewCompletedProcedure: false,
  canApplyApplication: false,
  canCancel: false,
  canEdit: false,
  canEditApplication: false,
  canRecall: false,
  canReview: false,
  canSumming: false,
  canView: false,
  canCopy: false,
  canViewWinners: false,
});
const loadingDialogText = ref<string>();
const procedureDocuments = ref<object[]>([]);
const procedureDocumentsProject = ref<object[]>([]);
const agreementDocuments = ref<object[]>([]);
const winners = reactive([]);
const result = reactive({
  isCurrentContragentWinner: false,
  cancellationReasons: [],
});
const isAdmin = config.getUser().hasRole('ROLE_ADMIN');
const isOrg = config.getUser().hasRole('ROLE_CUSTOMER');
const isHiddenContractPerson = config.getConfig('HIDDEN_CONTACT_PERSON');
const showStartMaxPrice = ref<boolean>(false);
const showTitleHint = ref<boolean>(true);
const showPriceQuantity = ref<boolean>(false);
const tableReplaceCaption = ref<string>('');

const actionsInfo = computed<object>(
  () => ({
    p: {
      id: procedure.id,
      registryNumber: procedure.registryNumber
    },
    access,
    procedureActions,
  })
);

const canViewOpenCloseInfo = computed(() => {
  const user = Config.getUser();
  return user.hasRole('ROLE_ADMIN') || user.hasRole('ROLE_CUSTOMER');
});

const viewStageComponent = computed(() => {
  const viewStageStore = useViewProcedureStageComponentStore();

  return viewStageStore.getComponentByType(procedure.type) || ProcedureStageInfo;
});

const isGuest = computed(() => {
  return Config.isGuest();
});

const libraryStore = useLibraryStore();
const procedureViewStore = useProcedureViewStore();

const load = async (procedureId, isPublicRoute) => {
  const url = isPublicRoute ? '/open/procedures/' + procedureId : '/procedures/view/' + procedureId;

  const resp = await api.get(url);
  Object.assign(winners, resp.winners);
  Object.assign(result, resp.result);
  Object.assign(categories, resp.categories);
  Object.assign(positions, resp.positions);
  Object.assign(procedure, resp.procedure);
  Object.assign(procedureActions, resp.procedureActions);
  Object.assign(access, resp.access);
  Object.assign(supplierRequirements, resp.supplierRequirements)
  agreementDocuments.value = resp.agreementDocuments;
  procedureDocuments.value = resp.procedureDocuments;
  procedureDocumentsProject.value = resp.procedureDocumentsProject;
  types.value = resp.types.map((elem) => ({
    title: elem.name,
    value: elem.code,
  }));

  libraryStore.callActionByCode('initProcedureViewOptions', {
    procedureType: procedure.type,
  });
  const data = libraryStore.callFunctionByCode('getPositionsViewOptions', {
    procedureType: procedure.type,
    procedureStep: procedure.status.code,
  });
  showStartMaxPrice.value = data.showStartMaxPrice ?? false;
  showTitleHint.value = data.showTitleHint ?? true;
  showPriceQuantity.value = data.showPriceQuantity ?? false;
};

const onLoadError = error => {
  if (error.name !== 403) {
    console.error(666, error.message);
  }
};

const downloadDocument = async (documentId: number) => {
  window.location.href = '/procedures/downloadDocument/' + documentId;
};

const downloadAgreementDocument = async (documentId: number) => {
  window.location.href = '/procedures/downloadAgreementDocument/' + documentId;
};

const downloadDocuments = async (procedureId: number, documentType: number) => {
  window.location.href = `/procedures/downloadDocuments/${procedureId}/${documentType}`;
};

const downloadAgreementDocuments = async (procedureId: number) => {
  window.location.href = '/procedures/downloadAgreementDocuments/' + procedureId;
};
</script>

<style lang="scss">
</style>
